import { patchWaitingSound } from './calls';
import { onPatchState, patchNow, type PatchState } from './patch';

/**
 * A soft chime while you wait for people to join a Patch you started (1.12.26, Teampace 2946; Joey:
 * "when you are starting a Patch and waiting for everyone to answer, we should play a nice sound").
 *
 * - It plays while this person is alone in a Patch they started, for as long as the others are rung
 *   (half a minute, as lib/patchRing and the server ring them).
 * - It stops as soon as anyone comes in (in the app, or on a desk phone), when everyone a direct
 *   message's Patch rang has said no, when the half minute is up, or when they leave.
 * - A phone being rung for the Patch while they're alone ("Ringing Dana's desk phone", "Calling
 *   Dana's phone") keeps it going, for a minute at most.
 * - The people being rung hear nothing new: they already get the Patch's ring.
 * - On unless turned off on this device (Your settings → Calls and audio). The chime itself is made
 *   with the ringing and ringback tones (lib/calls): another call ringing, or ringback for a call,
 *   comes first.
 */

/** As long as the others are rung (PATCH_RING_MS on the server, RING_MS in lib/patchRing). */
export const WAIT_MS = 30_000;
/** A phone rung for the Patch: at most this long. */
export const PHONE_WAIT_MS = 60_000;

type Waiting = Pick<PatchState, 'phase' | 'starter' | 'people' | 'phones' | 'declined' | 'rung' | 'startedAt'>;

/**
 * Whether the person in this Patch is waiting for others to join (and so hears the chime).
 * `phoneSince`: when a phone first started ringing for it (this device's clock), or null.
 */
export function waitingForOthers(s: Waiting, now: number, phoneSince: number | null): boolean {
  if (s.phase !== 'connected') return false;
  // Someone came in: in the app, or on a desk phone.
  if (s.people.some((p) => !p.me) || s.phones.some((p) => p.state === 'active')) return false;
  if (s.phones.some((p) => p.state === 'ringing') && phoneSince !== null && now - phoneSince < PHONE_WAIT_MS) return true;
  if (!s.starter) return false;
  const everyoneSaidNo = s.rung.length > 0 && s.rung.every((id) => s.declined.some((d) => d.id === id));
  return !everyoneSaidNo && now - s.startedAt < WAIT_MS;
}

// --- this device's choice -------------------------------------------------------------------------

const SOUND_KEY = 'stitch.sound.patchWaiting';
/** The Patch waiting chime on this device (on unless turned off here). */
export function waitingSoundOn(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}
export function setWaitingSoundOn(on: boolean): void {
  try {
    if (on) localStorage.removeItem(SOUND_KEY);
    else localStorage.setItem(SOUND_KEY, 'off');
  } catch {
    // Private browsing: it lasts until the page closes.
  }
  check();
}

// --- following the Patch ----------------------------------------------------------------------------

let phoneSince: number | null = null;
let timer: number | undefined;

function check(): void {
  if (typeof window === 'undefined') return;
  const s = patchNow();
  const now = Date.now();
  if (s.phase !== 'connected' || !s.phones.some((p) => p.state === 'ringing')) phoneSince = null;
  else phoneSince ??= now;
  const on = waitingSoundOn() && waitingForOthers(s, now, phoneSince);
  patchWaitingSound(on);
  window.clearTimeout(timer);
  // While it plays, look again each second: the half minute ends without the Patch changing.
  if (on) timer = window.setTimeout(check, 1000);
}

if (typeof window !== 'undefined') {
  onPatchState(check);
  window.addEventListener('stitch:signed-out', () => patchWaitingSound(false));
}
